import { Card } from '../atoms/Card/Card';
import { Badge } from '../atoms/Badge/Badge';
import type { BadgeTone } from '../atoms/Badge/Badge.types';
import { Button } from '../atoms/Button/Button';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { deriveStatus, type MeasurementStatus } from '../../hooks/useLatestMeasurements';
import type { ParameterTarget } from '../../services/measurementService';
import type { AttachedMeasurement } from '../../services/journalService';
import { useTranslation } from '../../i18n/useTranslation';
import { formatMeasuredAt, measuredAtDateTimeAttr } from '../../utils/measurementDate';
import './MeasurementCardGrid.css';

export interface MeasurementCardGridProps {
  measurements: readonly AttachedMeasurement[];
  /** When given, each card gets an unlink action. */
  onUnlink?: (measurementId: string) => void;
  /** Id of the measurement currently being unlinked (its action shows a spinner). */
  unlinkingId?: string | null;
  /** The aquarium's resolved targets by parameter id (useParameterTargets); without them the status is unknown. */
  targets?: ReadonlyMap<string, ParameterTarget>;
}

/**
 * Status of a measurement against the aquarium's resolved targets (personal set
 * point or hobby default, via deriveStatus). Tries the parameter id first, then
 * the display name.
 */
export function getMeasurementStatus(
  measurement: AttachedMeasurement,
  targets: ReadonlyMap<string, ParameterTarget> | undefined,
): MeasurementStatus {
  const lookup = (key: string) => targets?.get(key.trim().toLowerCase());
  const byId = deriveStatus(lookup(measurement.parameterId), measurement.value);
  return byId !== 'unknown' ? byId : deriveStatus(lookup(measurement.parameterName), measurement.value);
}

/** Colour (Badge tone) + a glyph + the status text — never colour alone. */
function statusPresentation(status: MeasurementStatus): { tone: BadgeTone; glyph: string } {
  switch (status) {
    case 'excellent':
      return { tone: 'success', glyph: '✓' };
    case 'stable':
      return { tone: 'info', glyph: '●' };
    case 'warning':
      return { tone: 'warning', glyph: '!' };
    case 'critical':
      return { tone: 'danger', glyph: '✕' };
    default:
      return { tone: 'neutral', glyph: '?' };
  }
}


/** Measurements attached to a journal entry, newest first, as a responsive grid of cards. */
export function MeasurementCardGrid({ measurements, onUnlink, unlinkingId = null, targets }: MeasurementCardGridProps) {
  const { t, i18n } = useTranslation();

  const sorted = [...measurements].sort(
    (a, b) => new Date(b.measuredAt).getTime() - new Date(a.measuredAt).getTime(),
  );

  return (
    <ul className="measurement-card-grid" data-testid="measurement-card-grid" aria-label={t('journal.measurements.title')}>
      {sorted.map((measurement) => {
        const status = getMeasurementStatus(measurement, targets);
        const { tone, glyph } = statusPresentation(status);
        return (
          <li key={measurement.id}>
            <Card padding="md" className="measurement-card" data-testid="measurement-card">
              <Heading level={3} className="measurement-card__parameter">
                {measurement.parameterName}
              </Heading>
              <Text className="measurement-card__value">
                <span className="measurement-card__number">{measurement.value}</span>
                {measurement.unit && <span className="measurement-card__unit"> {measurement.unit}</span>}
              </Text>
              <Text size="sm" color="secondary">
                <time dateTime={measuredAtDateTimeAttr(measurement)}>{formatMeasuredAt(measurement, i18n.language, 'short')}</time>
              </Text>
              <Badge tone={tone} className="measurement-card__status" data-testid="measurement-status" data-status={status}>
                <span aria-hidden="true">{glyph}</span> {t(`journal.measurements.status.${status}`)}
              </Badge>
              {onUnlink && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="measurement-card__unlink"
                  isLoading={unlinkingId === measurement.id}
                  disabled={unlinkingId !== null}
                  aria-label={t('journal.measurements.unlinkLabel', { parameter: measurement.parameterName })}
                  onClick={() => onUnlink(measurement.id)}
                >
                  {t('journal.measurements.unlink')}
                </Button>
              )}
            </Card>
          </li>
        );
      })}
    </ul>
  );
}
